import {
  ASSET_KIND_LABELS,
  ASSET_KINDS,
  type Asset,
  type AssetCutout,
  type AssetFolder,
  type AssetKind,
  childFolders,
  cutoutBlocker,
  folderPath,
  folderSubtree,
  formatBytes,
  MEDIA_ROOT_LABEL,
  UPLOAD_ACCEPT,
  wouldCreateCycle,
} from "@cva/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  AudioLines,
  Check,
  CheckSquare,
  ChevronRight,
  CircleAlert,
  Download,
  ExternalLink,
  Film,
  Folder,
  FolderInput,
  FolderPlus,
  ImageIcon,
  Images,
  Loader2,
  MoreHorizontal,
  Pencil,
  Search,
  Shapes,
  Smartphone,
  Trash2,
  Upload,
  Wand2,
  X,
} from "lucide-react";
import {
  type DragEvent,
  Fragment,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  ASSETS_DRAG_TYPE,
  countLabel,
  FOLDER_DRAG_TYPE,
  FolderNameDialog,
  FolderPickerDialog,
  FolderSelect,
  folderPathLabel,
  useMediaFolders,
} from "../components/media-folders";
import { MockupDialog } from "../components/mockup-dialog";
import { Button } from "../components/ui/button";
import {
  Badge,
  EmptyState,
  PageHeader,
  Progress,
  Skeleton,
  skeletonKeys,
} from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm";
import { ContextMenuPopup, useContextMenu } from "../components/ui/context-menu";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import {
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSeparator,
  DropdownTrigger,
} from "../components/ui/dropdown";
import { Field, Input, Select } from "../components/ui/input";
import { estimateProgress, useElapsed } from "../lib/activity";
import { client, orpc } from "../lib/api";
import { requestCutout } from "../lib/cutout";
import { useOrgId, useOrgRole } from "../lib/org";
import { onUploadFinished, startUpload } from "../lib/uploads";
import { cn, errorMessage, formatDateTime, formatDuration } from "../lib/utils";

const PAGE_SIZE = 60;

const KIND_ICONS: Record<AssetKind, typeof ImageIcon> = {
  image: ImageIcon,
  video: Film,
  audio: AudioLines,
  svg: Shapes,
};

/** Transparent images (cut-outs) show on a checkerboard. */
const CHECKERBOARD =
  "bg-[repeating-conic-gradient(#f4f4f5_0%_25%,#fff_0%_50%)] bg-[length:12px_12px]";

function AssetThumb({ asset, className }: { asset: Asset; className?: string }) {
  const Icon = KIND_ICONS[asset.kind];
  if (asset.thumbnailUrl) {
    return (
      <img
        src={asset.thumbnailUrl}
        alt=""
        loading="lazy"
        draggable={false}
        // The whole subject of a cut-out or mockup, not a crop of it.
        className={cn(
          "size-full",
          asset.cutout || asset.mockup ? "object-contain p-2" : "object-cover",
          className,
        )}
      />
    );
  }
  if (asset.status === "processing" && asset.cutout) {
    return (
      <div
        className={cn(
          "flex size-full flex-col items-center justify-center gap-2 px-4 text-center text-xs text-brand-800",
          className,
        )}
      >
        <Wand2 className="size-6 animate-pulse text-brand-500" />
        <CutoutProgressText cutout={asset.cutout} />
      </div>
    );
  }
  if (asset.status === "processing" && asset.mockup) {
    return (
      <div
        className={cn(
          "flex size-full flex-col items-center justify-center gap-2 px-4 text-center text-xs text-brand-800",
          className,
        )}
      >
        <Smartphone className="size-6 animate-pulse text-brand-500" />
        <p>Mockup wird erstellt …</p>
      </div>
    );
  }
  return (
    <div className={cn("flex size-full items-center justify-center text-zinc-400", className)}>
      {asset.status === "processing" ? (
        <Loader2 className="size-6 animate-spin" />
      ) : (
        <Icon className="size-8" />
      )}
    </div>
  );
}

/** "Wird freigestellt … 40 %" with the progress estimated from the expected duration. */
function CutoutProgressText({ cutout }: { cutout: AssetCutout }) {
  const elapsed = useElapsed(cutout.elapsedMs);
  const estimate = estimateProgress(cutout.progress ?? 0, elapsed, cutout.estimatedMs);
  const started = cutout.elapsedMs !== null;
  return (
    <div className="w-full space-y-1.5">
      <p>{started ? "Hintergrund wird entfernt …" : "Wartet auf das Freistellen …"}</p>
      {started ? <Progress className="h-1.5" value={estimate.progress} /> : null}
    </div>
  );
}

function AssetDialog({
  orgId,
  asset: initial,
  folders,
  onClose,
  onOpen,
}: {
  orgId: string;
  asset: Asset;
  folders: AssetFolder[];
  onClose: () => void;
  /** Shows another file in the dialog (the cut-out copy or its original). */
  onOpen: (asset: Asset) => void;
}) {
  const queryClient = useQueryClient();
  const { can } = useOrgRole(orgId);
  // Live: a cut-out copy that is still being made shows its progress and then the result.
  const live = useQuery({
    ...orpc.media.get.queryOptions({ input: { orgId, assetId: initial.id } }),
    initialData: initial,
    refetchInterval: (query) => (query.state.data?.status === "processing" ? 1500 : false),
  });
  const asset = live.data ?? initial;
  const [name, setName] = useState(asset.name);
  const [mockup, setMockup] = useState(false);
  const [projectId, setProjectId] = useState(asset.projectId ?? "");
  const [folderId, setFolderId] = useState(asset.folderId);
  const projects = useQuery(
    orpc.projects.list.queryOptions({ input: { orgId, includeArchived: false } }),
  );
  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.media.key() });
  const update = useMutation(
    orpc.media.update.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        toast.success("Gespeichert");
        onClose();
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  const remove = useMutation(
    orpc.media.remove.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        toast.success("In den Papierkorb verschoben");
        onClose();
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  const cutOut = useMutation({
    mutationFn: () => requestCutout(queryClient, orgId, asset.id),
    onSuccess: (copy) => onOpen(copy),
    onError: (error) => toast.error(errorMessage(error)),
  });
  const openOriginal = useMutation({
    mutationFn: async (sourceId: string) => {
      const source = await client.media.get({ orgId, assetId: sourceId }).catch(() => null);
      if (!source) throw new Error("Das Original liegt im Papierkorb oder wurde gelöscht.");
      return source;
    },
    onSuccess: (source) => onOpen(source),
    onError: (error) => toast.error(errorMessage(error)),
  });
  const canCutOut =
    can("media.upload") && asset.kind === "image" && !asset.mockup && !cutoutBlocker(asset);
  const processingCutout = asset.cutout && asset.status === "processing" ? asset.cutout : null;
  const processingMockup = asset.mockup && asset.status === "processing" ? asset.mockup : null;
  const processing = processingCutout !== null || processingMockup !== null;
  const details: [string, string][] = [
    ["Typ", `${ASSET_KIND_LABELS[asset.kind]} (${asset.mime})`],
    ["Datei", asset.originalName],
  ];
  // A cut-out copy or mockup has no file yet while it is being made.
  if (!processing) {
    details.push(["Größe", formatBytes(asset.sizeBytes)]);
    details.push(["Inkl. Vorschauen", formatBytes(asset.totalBytes)]);
  }
  if (asset.width && asset.height)
    details.push(["Abmessungen", `${asset.width} × ${asset.height} px`]);
  if (asset.durationMs) details.push(["Dauer", formatDuration(asset.durationMs)]);
  details.push([
    asset.cutout ? "Freigestellt" : asset.mockup ? "Erstellt" : "Hochgeladen",
    `${formatDateTime(asset.createdAt)}${asset.createdBy ? ` von ${asset.createdBy.name}` : ""}`,
  ]);
  if (!can("media.update")) details.push(["Ordner", folderPathLabel(folders, asset.folderId)]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={asset.name}
        className="max-h-[calc(100dvh-2rem)] max-w-4xl overflow-y-auto"
      >
        <div className="grid gap-6 md:grid-cols-[1fr_260px]">
          <div className="min-w-0 space-y-3">
            <div
              className={cn(
                "flex max-h-[60vh] min-h-64 items-center justify-center overflow-hidden rounded-lg",
                asset.cutout || asset.mockup ? CHECKERBOARD : "bg-zinc-100",
              )}
            >
              {processingMockup ? (
                <div className="flex w-64 flex-col items-center gap-3 text-center text-sm text-brand-900">
                  <Smartphone className="size-8 animate-pulse text-brand-500" />
                  <p>Mockup wird erstellt …</p>
                  <p className="text-xs text-zinc-500">
                    Du kannst den Dialog schließen – das Bild erscheint in der Mediathek, sobald es
                    fertig ist.
                  </p>
                </div>
              ) : processingCutout ? (
                <div className="flex w-64 flex-col items-center gap-3 text-center text-sm text-brand-900">
                  <Wand2 className="size-8 animate-pulse text-brand-500" />
                  <CutoutProgressText cutout={processingCutout} />
                  <p className="text-xs text-zinc-500">
                    Du kannst den Dialog schließen – die freigestellte Kopie erscheint in der
                    Mediathek, sobald sie fertig ist.
                  </p>
                </div>
              ) : asset.kind === "video" ? (
                // biome-ignore lint/a11y/useMediaCaption: user uploaded media without captions
                <video
                  src={asset.url}
                  controls
                  className="max-h-[60vh] w-full"
                  poster={asset.thumbnailUrl ?? undefined}
                />
              ) : asset.kind === "audio" ? (
                // biome-ignore lint/a11y/useMediaCaption: user uploaded media without captions
                <audio src={asset.url} controls className="w-full px-4" />
              ) : (
                <img src={asset.url} alt={asset.name} className="max-h-[60vh] object-contain" />
              )}
            </div>
            {/* What can be done with the file itself; the footer only saves or discards it. */}
            <div className="flex flex-wrap gap-2">
              {processing ? null : (
                <Button variant="secondary" size="sm" asChild>
                  <a href={asset.url} download={asset.originalName}>
                    <Download /> Herunterladen
                  </a>
                </Button>
              )}
              {canCutOut ? (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={cutOut.isPending}
                  onClick={() => cutOut.mutate()}
                  title="Eine Kopie ohne Hintergrund anlegen – das Original bleibt."
                >
                  <Wand2 /> Hintergrund entfernen
                </Button>
              ) : null}
              {can("document.create") &&
              asset.kind === "image" &&
              asset.status === "ready" &&
              !asset.mockup ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setMockup(true)}
                  title="Den Screenshot in einem iPhone, MacBook, Browserfenster … zeigen"
                >
                  <Smartphone /> Mockup erstellen
                </Button>
              ) : null}
            </div>
          </div>
          <div className="space-y-4">
            {asset.status === "failed" ? (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{asset.error}</p>
            ) : null}
            {asset.cutout ? (
              <div className="rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-900">
                <p className="flex items-center gap-1.5 font-medium">
                  <Wand2 className="size-4" /> Freigestellte Kopie
                </p>
                <p className="mt-0.5 text-xs text-brand-800">
                  Der Hintergrund ist durchsichtig, das Original bleibt unverändert.
                </p>
                <button
                  type="button"
                  disabled={openOriginal.isPending}
                  onClick={() => asset.cutout && openOriginal.mutate(asset.cutout.sourceId)}
                  className="mt-1 text-xs font-medium text-brand-700 hover:underline disabled:opacity-50"
                >
                  Original öffnen
                </button>
              </div>
            ) : null}
            {asset.mockup ? (
              <div className="rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-900">
                <p className="flex items-center gap-1.5 font-medium">
                  <Smartphone className="size-4" /> Mockup · {asset.mockup.deviceName}
                </p>
                <p className="mt-0.5 text-xs text-brand-800">
                  Nur das Gerät, ohne Hintergrund – der Screenshot bleibt unverändert.
                </p>
                <button
                  type="button"
                  disabled={openOriginal.isPending}
                  onClick={() => asset.mockup && openOriginal.mutate(asset.mockup.sourceId)}
                  className="mt-1 text-xs font-medium text-brand-700 hover:underline disabled:opacity-50"
                >
                  Screenshot öffnen
                </button>
              </div>
            ) : null}
            <dl className="space-y-1.5 text-sm">
              {details.map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs text-zinc-500">{label}</dt>
                  <dd className="break-words text-zinc-800">{value}</dd>
                </div>
              ))}
            </dl>
            {can("media.update") ? (
              <>
                <Field label="Name">
                  <Input value={name} onChange={(e) => setName(e.target.value)} />
                </Field>
                <Field label="Ordner">
                  <FolderSelect folders={folders} value={folderId} onChange={setFolderId} />
                </Field>
                <Field label="Projekt">
                  <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                    <option value="">Ohne Projekt (ganze Organisation)</option>
                    {projects.data?.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </>
            ) : null}
          </div>
        </div>
        {can("media.delete") || can("media.update") ? (
          <DialogFooter>
            {can("media.delete") ? (
              <Button
                variant="ghost"
                className="mr-auto text-red-600 hover:bg-red-50 hover:text-red-700"
                loading={remove.isPending}
                onClick={() => remove.mutate({ orgId, assetId: asset.id })}
              >
                <Trash2 /> In den Papierkorb
              </Button>
            ) : null}
            {can("media.update") ? (
              <Button
                loading={update.isPending}
                onClick={() =>
                  update.mutate({
                    orgId,
                    assetId: asset.id,
                    name: name.trim() || asset.name,
                    projectId: projectId || null,
                    folderId,
                  })
                }
              >
                Speichern
              </Button>
            ) : null}
          </DialogFooter>
        ) : null}
        {mockup ? (
          <MockupDialog
            orgId={orgId}
            asset={asset}
            defaultProjectId={asset.projectId}
            onSaved={can("media.upload") ? onOpen : undefined}
            onClose={() => setMockup(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Drag & drop: media and folders onto folder tiles and breadcrumb entries
// ---------------------------------------------------------------------------

type DropPayload = { assetIds: string[] } | { folderId: string };
type DropAccepts = (target: string | null, types: readonly string[]) => boolean;
type DropHandler = (payload: DropPayload, target: string | null) => void;

function useFolderDrop(target: string | null, accepts: DropAccepts, onDrop: DropHandler) {
  const [over, setOver] = useState(false);
  // dragenter/dragleave also fire for child elements.
  const depth = useRef(0);
  const ok = (event: DragEvent) => accepts(target, event.dataTransfer.types);
  return {
    over,
    handlers: {
      onDragEnter: (event: DragEvent) => {
        if (!ok(event)) return;
        event.preventDefault();
        depth.current += 1;
        setOver(true);
      },
      onDragOver: (event: DragEvent) => {
        if (!ok(event)) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = "move";
      },
      onDragLeave: () => {
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      },
      onDrop: (event: DragEvent) => {
        depth.current = 0;
        setOver(false);
        if (!ok(event)) return;
        event.preventDefault();
        event.stopPropagation();
        const assets = event.dataTransfer.getData(ASSETS_DRAG_TYPE);
        const folder = event.dataTransfer.getData(FOLDER_DRAG_TYPE);
        if (assets) onDrop({ assetIds: JSON.parse(assets) as string[] }, target);
        else if (folder) onDrop({ folderId: folder }, target);
      },
    },
  };
}

/** Small label under the cursor instead of the tile screenshot when dragging several files. */
function setDragLabel(event: DragEvent, label: string) {
  const badge = document.createElement("div");
  badge.textContent = label;
  badge.className =
    "fixed -top-40 left-0 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white shadow-lg";
  document.body.append(badge);
  event.dataTransfer.setDragImage(badge, 16, 16);
  setTimeout(() => badge.remove(), 0);
}

function Crumb({
  label,
  target,
  accepts,
  onDrop,
  onOpen,
}: {
  label: string;
  target: string | null;
  accepts: DropAccepts;
  onDrop: DropHandler;
  onOpen: () => void;
}) {
  const drop = useFolderDrop(target, accepts, onDrop);
  return (
    <button
      type="button"
      onClick={onOpen}
      {...drop.handlers}
      className={cn(
        "-mx-1 max-w-56 truncate rounded-md px-1 text-zinc-400 transition hover:text-zinc-900",
        drop.over && "bg-brand-50 text-brand-700 ring-2 ring-brand-500/40",
      )}
    >
      {label}
    </button>
  );
}

function FolderTile({
  folder,
  subfolders,
  menu,
  draggable,
  accepts,
  onDrop,
  onOpen,
  onDragStart,
  onDragEnd,
  onContextMenu,
}: {
  folder: AssetFolder;
  subfolders: number;
  menu: ReactNode;
  onContextMenu: (event: MouseEvent) => void;
  draggable: boolean;
  accepts: DropAccepts;
  onDrop: DropHandler;
  onOpen: () => void;
  onDragStart: (event: DragEvent) => void;
  onDragEnd: () => void;
}) {
  const drop = useFolderDrop(folder.id, accepts, onDrop);
  const summary = [
    subfolders > 0 ? countLabel(subfolders, "Ordner", "Ordner") : null,
    folder.assetCount > 0 ? countLabel(folder.assetCount, "Datei", "Dateien") : null,
  ].filter(Boolean);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drop target, the button inside opens the folder
    <div
      {...drop.handlers}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onContextMenu={onContextMenu}
      className={cn(
        "group relative flex items-center gap-3 rounded-xl border border-zinc-200 bg-white py-2.5 pr-1.5 pl-3 shadow-xs transition hover:border-brand-300 hover:shadow-md",
        drop.over && "border-brand-500 bg-brand-50 ring-2 ring-brand-500/30",
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-3 text-left after:absolute after:inset-0 after:rounded-xl"
      >
        <Folder className="size-8 shrink-0 fill-brand-100 text-brand-600" strokeWidth={1.5} />
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-zinc-900">{folder.name}</span>
          <span className="block truncate text-xs text-zinc-500">
            {summary.length > 0 ? summary.join(" · ") : "Leer"}
          </span>
        </span>
      </button>
      {menu ? <div className="relative z-10">{menu}</div> : null}
    </div>
  );
}

function AssetTile({
  asset,
  selected,
  selectable,
  selecting,
  folderLabel,
  onOpen,
  onToggle,
  onDragStart,
  onOpenFolder,
  onContextMenu,
  draggable,
}: {
  asset: Asset;
  selected: boolean;
  selectable: boolean;
  draggable: boolean;
  onContextMenu: (event: MouseEvent) => void;
  /** Some tile is selected: checkboxes stay visible. */
  selecting: boolean;
  /** Search results: where the file lives. */
  folderLabel: string | null;
  onOpen: (event: MouseEvent) => void;
  onToggle: (event: MouseEvent) => void;
  onDragStart: (event: DragEvent) => void;
  onOpenFolder: () => void;
}) {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drag source, the button inside opens the file
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onContextMenu={onContextMenu}
      className={cn(
        "group relative overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xs transition hover:border-brand-300 hover:shadow-md",
        selected && "border-brand-500 ring-2 ring-brand-500/40 hover:border-brand-500",
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        // Shift-click selects a range; the browser should not select text.
        onMouseDown={(event) => event.shiftKey && event.preventDefault()}
        className="block w-full text-left"
      >
        <div className={cn("relative aspect-square", asset.cutout ? CHECKERBOARD : "bg-zinc-100")}>
          <AssetThumb asset={asset} />
          {asset.kind === "video" && asset.durationMs ? (
            <span className="absolute right-2 bottom-2 rounded bg-black/70 px-1.5 py-0.5 text-xs text-white">
              {formatDuration(asset.durationMs)}
            </span>
          ) : null}
          {asset.status === "failed" ? (
            <span className="absolute top-2 right-2">
              <Badge tone="danger">
                <CircleAlert className="mr-1 size-3" /> Fehler
              </Badge>
            </span>
          ) : asset.cutout && asset.status === "ready" ? (
            <span className="absolute top-2 right-2">
              <Badge tone="brand" className="shadow-xs">
                <Wand2 className="mr-1 size-3" /> Freigestellt
              </Badge>
            </span>
          ) : null}
        </div>
        <div className={cn("px-3 pt-2.5", folderLabel === null && "pb-2.5")}>
          <p className="truncate text-sm font-medium">{asset.name}</p>
          <p className="mt-0.5 text-xs text-zinc-500">
            {formatBytes(asset.sizeBytes)}
            {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ""}
          </p>
        </div>
      </button>
      {folderLabel !== null ? (
        <button
          type="button"
          onClick={onOpenFolder}
          title="Ordner öffnen"
          className="mx-3 mt-0.5 mb-2.5 flex max-w-[calc(100%-1.5rem)] items-center gap-1 text-xs text-zinc-500 hover:text-brand-700"
        >
          <Folder className="size-3 shrink-0" />
          <span className="truncate">{folderLabel}</span>
        </button>
      ) : null}
      {selectable ? (
        <button
          type="button"
          aria-pressed={selected}
          aria-label={`${asset.name} auswählen`}
          onClick={onToggle}
          onMouseDown={(event) => event.shiftKey && event.preventDefault()}
          className={cn(
            "absolute top-2 left-2 flex size-6 items-center justify-center rounded-md border-2 shadow-sm transition focus-visible:opacity-100",
            selected
              ? "border-brand-600 bg-brand-600 text-white"
              : "border-white bg-white/80 text-transparent opacity-0 group-hover:opacity-100 hover:text-zinc-400",
            selecting && "opacity-100",
          )}
        >
          <Check className="size-4" strokeWidth={3} />
        </button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function MediaPage() {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useOrgRole(orgId);
  const canMove = can("media.update");
  const canDelete = can("media.delete");
  // Selecting is for moving or deleting several files at once.
  const canSelect = canMove || canDelete;
  const fileInput = useRef<HTMLInputElement>(null);
  const {
    folder: folderParam,
    q: searchParam,
    open: openParam,
  } = useSearch({ strict: false }) as { folder?: string; q?: string; open?: string };
  const { folders, loaded } = useMediaFolders(orgId);
  const [kind, setKind] = useState<AssetKind | undefined>();
  const [projectId, setProjectId] = useState("");
  const [search, setSearch] = useState(searchParam ?? "");
  const [debounced, setDebounced] = useState(searchParam ?? "");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [dragging, setDragging] = useState(false);
  const [opened, setOpened] = useState<Asset | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const anchor = useRef<string | null>(null);
  const draggedFolder = useRef<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<AssetFolder | null>(null);
  const [deleting, setDeleting] = useState<AssetFolder | null>(null);
  const [movingFolder, setMovingFolder] = useState<AssetFolder | null>(null);
  const [movingAssets, setMovingAssets] = useState<string[] | null>(null);
  const [mockupFor, setMockupFor] = useState<Asset | null>(null);
  const assetMenu = useContextMenu<Asset>();
  const folderContext = useContextMenu<AssetFolder>();
  const pageMenu = useContextMenu();
  const projects = useQuery(
    orpc.projects.list.queryOptions({ input: { orgId, includeArchived: true } }),
  );

  const current = folders.find((folder) => folder.id === folderParam) ?? null;
  // Until the folders are loaded the URL is trusted, so the page does not jump.
  const folderId = folderParam && (!loaded || current) ? folderParam : null;
  const path = folderPath(folders, folderId);
  const subfolders = childFolders(folders, folderId);
  // Search and the project filter look into every folder.
  const global = Boolean(debounced.trim() || projectId);

  function openFolder(id: string | null, replace = false) {
    void navigate({
      to: "/o/$orgId/media",
      params: { orgId },
      search: id ? { folder: id } : {},
      replace,
    });
  }

  // A deleted folder (old link, other tab) opens the top level.
  const missing = loaded && Boolean(folderParam) && !current;
  useEffect(() => {
    if (missing) void navigate({ to: "/o/$orgId/media", params: { orgId }, replace: true });
  }, [missing, navigate, orgId]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  // Another folder or filter: start with an empty selection and the first page.
  const scope = `${folderId}|${kind}|${projectId}|${debounced}`;
  const [lastScope, setLastScope] = useState(scope);
  if (scope !== lastScope) {
    setLastScope(scope);
    setSelected(new Set());
    setLimit(PAGE_SIZE);
  }

  const media = useQuery({
    ...orpc.media.list.queryOptions({
      input: {
        orgId,
        kind,
        projectId: projectId || undefined,
        folderId: global ? undefined : folderId,
        search: debounced || undefined,
        limit,
        offset: 0,
      },
    }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      query.state.data?.items.some((item) => item.status === "processing") ? 2500 : false,
  });

  // From the search (⌘K): open the found file once it is in the list.
  const found = openParam ? media.data?.items.find((item) => item.id === openParam) : undefined;
  useEffect(() => {
    if (!found) return;
    setOpened(found);
    void navigate({ to: "/o/$orgId/media", params: { orgId }, search: {}, replace: true });
  }, [found, navigate, orgId]);

  useEffect(
    () =>
      onUploadFinished((item) => {
        if (item.orgId === orgId)
          void queryClient.invalidateQueries({ queryKey: orpc.media.key() });
      }),
    [orgId, queryClient],
  );

  const selecting = selected.size > 0;
  const dialogOpen = Boolean(
    opened || movingAssets || movingFolder || renaming || deleting || mockupFor,
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.media.key() });
  const removeMany = useMutation(orpc.media.removeMany.mutationOptions());

  /** Into the trash, with „Rückgängig“ in the message instead of asking first. */
  async function trashAssets(assetIds: string[]) {
    if (assetIds.length === 0) return;
    try {
      const result = await removeMany.mutateAsync({ orgId, assetIds });
      await invalidate();
      void queryClient.invalidateQueries({ queryKey: orpc.trash.key() });
      setSelected((current) => {
        const next = new Set(current);
        for (const id of assetIds) next.delete(id);
        return next;
      });
      toast.success(
        `${countLabel(result.removed, "Datei", "Dateien")} in den Papierkorb verschoben`,
        {
          duration: 8000,
          action: { label: "Rückgängig", onClick: () => void restoreAssets(assetIds) },
        },
      );
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }

  async function restoreAssets(assetIds: string[]) {
    try {
      await Promise.all(assetIds.map((id) => client.trash.restore({ orgId, type: "asset", id })));
      await invalidate();
      void queryClient.invalidateQueries({ queryKey: orpc.trash.key() });
      toast.success(`${countLabel(assetIds.length, "Datei", "Dateien")} wiederhergestellt`);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }

  // Keyboard with a selection: Esc clears it, Entf / ⌫ moves it into the trash.
  useEffect(() => {
    if (!selecting || dialogOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if ((event.target as HTMLElement | null)?.closest("input, textarea, select")) return;
      if (event.key === "Escape") setSelected(new Set());
      if ((event.key === "Delete" || event.key === "Backspace") && canDelete) {
        event.preventDefault();
        void trashAssets([...selected]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const moveAssets = useMutation(orpc.media.move.mutationOptions());
  const updateFolder = useMutation(orpc.media.folders.update.mutationOptions());
  const removeFolder = useMutation(
    orpc.media.folders.remove.mutationOptions({
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );

  const folderName = (id: string | null) =>
    id ? (folders.find((folder) => folder.id === id)?.name ?? "Ordner") : MEDIA_ROOT_LABEL;

  async function moveAssetsTo(assetIds: string[], target: string | null) {
    try {
      const result = await moveAssets.mutateAsync({ orgId, assetIds, folderId: target });
      await invalidate();
      setSelected(new Set());
      setMovingAssets(null);
      toast.success(
        `${countLabel(result.moved, "Datei", "Dateien")} nach „${folderName(target)}“ verschoben`,
      );
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }

  async function moveFolderTo(folder: AssetFolder, target: string | null) {
    try {
      await updateFolder.mutateAsync({ orgId, folderId: folder.id, parentId: target });
      await invalidate();
      setMovingFolder(null);
      toast.success(`„${folder.name}“ nach „${folderName(target)}“ verschoben`);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }

  const acceptsDrop: DropAccepts = (target, types) => {
    if (!canMove) return false;
    if (types.includes(ASSETS_DRAG_TYPE)) return true;
    const dragged = folders.find((folder) => folder.id === draggedFolder.current);
    return (
      types.includes(FOLDER_DRAG_TYPE) &&
      dragged !== undefined &&
      dragged.parentId !== target &&
      !wouldCreateCycle(folders, dragged.id, target)
    );
  };

  const handleDrop: DropHandler = (payload, target) => {
    if ("assetIds" in payload) void moveAssetsTo(payload.assetIds, target);
    else {
      const folder = folders.find((entry) => entry.id === payload.folderId);
      if (folder) void moveFolderTo(folder, target);
    }
  };

  function upload(files: FileList | File[]) {
    for (const file of Array.from(files))
      startUpload(file, { orgId, projectId: projectId || undefined, folderId });
  }

  // Only files from the computer are uploads; tiles dragged inside the page are moves.
  const isFileDrag = (event: DragEvent) =>
    event.dataTransfer.types.includes("Files") &&
    !event.dataTransfer.types.includes(ASSETS_DRAG_TYPE);

  function onDrop(event: DragEvent) {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    setDragging(false);
    if (can("media.upload") && event.dataTransfer.files.length) upload(event.dataTransfer.files);
  }

  const items = media.data?.items ?? [];
  const cutoutCandidates = can("media.upload")
    ? items.filter((item) => selected.has(item.id) && !cutoutBlocker(item))
    : [];
  const [cuttingOut, setCuttingOut] = useState(false);

  /** Background removal for every selected image; the copies appear next to them. */
  async function cutOutSelected() {
    setCuttingOut(true);
    let started = 0;
    let failure: unknown = null;
    try {
      for (const item of cutoutCandidates.slice(0, 50)) {
        try {
          await requestCutout(queryClient, orgId, item.id);
          started++;
        } catch (error) {
          failure ??= error;
        }
      }
    } finally {
      setCuttingOut(false);
    }
    if (started > 0) {
      setSelected(new Set());
      toast.success(
        `${countLabel(started, "Bild wird", "Bilder werden")} freigestellt – die Kopien erscheinen hier, sobald sie fertig sind.`,
      );
    }
    if (failure) toast.error(errorMessage(failure));
  }

  function toggleSelected(assetId: string, range: boolean) {
    setSelected((currentSelection) => {
      const next = new Set(currentSelection);
      const ids = items.map((item) => item.id);
      const from = anchor.current ? ids.indexOf(anchor.current) : -1;
      const to = ids.indexOf(assetId);
      if (range && from >= 0 && to >= 0) {
        for (const id of ids.slice(Math.min(from, to), Math.max(from, to) + 1)) next.add(id);
      } else if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
    anchor.current = assetId;
  }

  // ⌘A selects every file shown (outside of text fields).
  useEffect(() => {
    if (!canSelect || dialogOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "a") return;
      if ((event.target as HTMLElement | null)?.closest("input, textarea, select")) return;
      event.preventDefault();
      setSelected(new Set(items.map((item) => item.id)));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  function download(asset: Asset) {
    const link = document.createElement("a");
    link.href = asset.url;
    link.download = asset.originalName;
    document.body.append(link);
    link.click();
    link.remove();
  }

  async function cutOut(asset: Asset) {
    try {
      await requestCutout(queryClient, orgId, asset.id);
      toast.success("Wird freigestellt – die Kopie erscheint hier, sobald sie fertig ist.");
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }

  const canCutOut = (asset: Asset) =>
    can("media.upload") && asset.kind === "image" && !asset.mockup && !cutoutBlocker(asset);

  /** Right-click menu of a file; on a file of a larger selection it acts on the selection. */
  function assetMenuItems(asset: Asset) {
    if (selected.has(asset.id) && selected.size > 1) {
      const ids = [...selected];
      return (
        <>
          <DropdownLabel>{countLabel(ids.length, "Datei", "Dateien")} ausgewählt</DropdownLabel>
          {canMove ? (
            <DropdownItem onSelect={() => setMovingAssets(ids)}>
              <FolderInput /> Verschieben nach …
            </DropdownItem>
          ) : null}
          {cutoutCandidates.length > 0 ? (
            <DropdownItem onSelect={() => void cutOutSelected()}>
              <Wand2 /> Hintergrund entfernen ({cutoutCandidates.length})
            </DropdownItem>
          ) : null}
          <DropdownItem onSelect={() => setSelected(new Set())}>
            <X /> Auswahl aufheben
          </DropdownItem>
          {canDelete ? (
            <>
              <DropdownSeparator />
              <DropdownItem danger onSelect={() => void trashAssets(ids)}>
                <Trash2 /> {ids.length} in den Papierkorb
                <span className="ml-auto pl-6 text-xs text-zinc-400">Entf</span>
              </DropdownItem>
            </>
          ) : null}
        </>
      );
    }
    const ready = asset.status === "ready";
    return (
      <>
        <DropdownItem onSelect={() => setOpened(asset)}>
          <Pencil /> Öffnen &amp; umbenennen
        </DropdownItem>
        {ready ? (
          <>
            <DropdownItem onSelect={() => window.open(asset.url, "_blank", "noopener")}>
              <ExternalLink /> In neuem Tab ansehen
            </DropdownItem>
            <DropdownItem onSelect={() => download(asset)}>
              <Download /> Herunterladen
            </DropdownItem>
          </>
        ) : null}
        {canSelect ? (
          <DropdownItem onSelect={() => toggleSelected(asset.id, false)}>
            <CheckSquare /> {selected.has(asset.id) ? "Abwählen" : "Auswählen"}
          </DropdownItem>
        ) : null}
        {canMove ? (
          <DropdownItem onSelect={() => setMovingAssets([asset.id])}>
            <FolderInput /> Verschieben nach …
          </DropdownItem>
        ) : null}
        {canCutOut(asset) ||
        (can("document.create") && asset.kind === "image" && ready && !asset.mockup) ? (
          <DropdownSeparator />
        ) : null}
        {canCutOut(asset) ? (
          <DropdownItem onSelect={() => void cutOut(asset)}>
            <Wand2 /> Hintergrund entfernen
          </DropdownItem>
        ) : null}
        {can("document.create") && asset.kind === "image" && ready && !asset.mockup ? (
          <DropdownItem onSelect={() => setMockupFor(asset)}>
            <Smartphone /> Mockup erstellen …
          </DropdownItem>
        ) : null}
        {canDelete ? (
          <>
            <DropdownSeparator />
            <DropdownItem danger onSelect={() => void trashAssets([asset.id])}>
              <Trash2 /> In den Papierkorb
            </DropdownItem>
          </>
        ) : null}
      </>
    );
  }

  function onTileClick(asset: Asset, event: MouseEvent) {
    if (canSelect && (selecting || event.shiftKey || event.metaKey || event.ctrlKey)) {
      toggleSelected(asset.id, event.shiftKey);
      return;
    }
    setOpened(asset);
  }

  function folderMenuItems(folder: AssetFolder, withOpen = false) {
    return (
      <>
        {withOpen ? (
          <DropdownItem onSelect={() => openFolder(folder.id)}>
            <Folder /> Öffnen
          </DropdownItem>
        ) : null}
        {canMove ? (
          <>
            <DropdownItem onSelect={() => setRenaming(folder)}>
              <Pencil /> Umbenennen
            </DropdownItem>
            <DropdownItem onSelect={() => setMovingFolder(folder)}>
              <FolderInput /> Verschieben nach …
            </DropdownItem>
          </>
        ) : null}
        {canDelete ? (
          <>
            {canMove || withOpen ? <DropdownSeparator /> : null}
            <DropdownItem danger onSelect={() => setDeleting(folder)}>
              <Trash2 /> Ordner löschen
            </DropdownItem>
          </>
        ) : null}
      </>
    );
  }

  function folderMenu(folder: AssetFolder, header = false) {
    if (!canMove && !canDelete) return null;
    return (
      <DropdownMenu>
        <DropdownTrigger asChild>
          <Button
            variant={header ? "secondary" : "ghost"}
            size="icon"
            className={header ? undefined : "size-8 text-zinc-400"}
            aria-label={`Aktionen für Ordner ${folder.name}`}
          >
            <MoreHorizontal />
          </Button>
        </DropdownTrigger>
        <DropdownContent>{folderMenuItems(folder)}</DropdownContent>
      </DropdownMenu>
    );
  }

  const title =
    path.length === 0 ? (
      MEDIA_ROOT_LABEL
    ) : (
      <span className="flex min-w-0 items-center gap-1">
        {[{ id: null, name: MEDIA_ROOT_LABEL }, ...path.slice(0, -1)].map((entry) => (
          <Fragment key={entry.id ?? "root"}>
            <Crumb
              label={entry.name}
              target={entry.id}
              accepts={acceptsDrop}
              onDrop={handleDrop}
              onOpen={() => openFolder(entry.id)}
            />
            <ChevronRight className="size-5 shrink-0 text-zinc-300" />
          </Fragment>
        ))}
        <span className="truncate">{path.at(-1)!.name}</span>
      </span>
    );

  /** Deleting a folder never deletes media: say where its contents go. */
  function deleteHint(folder: AssetFolder) {
    const children = childFolders(folders, folder.id).length;
    const contents = [
      folder.assetCount > 0 ? countLabel(folder.assetCount, "Datei", "Dateien") : null,
      children > 0 ? countLabel(children, "Unterordner", "Unterordner") : null,
    ].filter(Boolean);
    if (contents.length === 0) return "Der Ordner ist leer.";
    const verb = folder.assetCount + children === 1 ? "landet" : "landen";
    return `Nur der Ordner wird gelöscht. ${contents.join(" und ")} darin ${verb} in „${folderName(folder.parentId)}“.`;
  }

  const showFolders = !global && subfolders.length > 0;
  const movingSelection = items.filter((item) => movingAssets?.includes(item.id));
  const movingFrom = new Set(movingSelection.map((item) => item.folderId));

  return (
    <section
      aria-label="Mediathek – Dateien zum Hochladen hier ablegen"
      onDragOver={(event) => {
        if (!isFileDrag(event)) return;
        event.preventDefault();
        if (can("media.upload")) setDragging(true);
      }}
      onDragLeave={(event) => {
        if (event.currentTarget === event.target) setDragging(false);
      }}
      onDrop={onDrop}
      onContextMenu={can("media.upload") || canMove || canSelect ? pageMenu.open(true) : undefined}
      className="relative min-h-[70vh]"
    >
      <PageHeader
        title={title}
        description={
          current
            ? "Dateien hierher ziehen, um sie in diesen Ordner hochzuladen. Medien lassen sich auf Ordner ziehen."
            : "Bilder, Videos, Audio und Grafiken deiner Organisation. Dateien einfach hierher ziehen."
        }
        actions={
          <>
            {current ? folderMenu(current, true) : null}
            {canMove ? (
              <Button variant="secondary" onClick={() => setCreating(true)}>
                <FolderPlus /> Neuer Ordner
              </Button>
            ) : null}
            {can("media.upload") ? (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  accept={UPLOAD_ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) upload(e.target.files);
                    e.target.value = "";
                  }}
                />
                <Button onClick={() => fileInput.current?.click()}>
                  <Upload /> Hochladen
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-zinc-200 bg-white p-0.5">
          {[undefined, ...ASSET_KINDS].map((value) => (
            <button
              key={value ?? "all"}
              type="button"
              onClick={() => setKind(value)}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium",
                kind === value ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900",
              )}
            >
              {value ? ASSET_KIND_LABELS[value] : "Alle"}
            </button>
          ))}
        </div>
        <Select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="w-56">
          <option value="">Alle Projekte</option>
          {projects.data?.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </Select>
        <div className="relative ml-auto w-64">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={folders.length > 0 ? "In allen Ordnern suchen …" : "Suchen …"}
            className="pl-9"
          />
        </div>
      </div>

      {global && folders.length > 0 ? (
        <p className="-mt-2 mb-4 text-sm text-zinc-500">
          Treffer aus allen Ordnern.
          {current ? <> Du bist im Ordner „{current.name}“, neue Uploads landen dort.</> : null}
        </p>
      ) : null}

      {selecting ? (
        <div className="sticky top-3 z-20 mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-brand-100 bg-white/95 px-2 py-1.5 shadow-lg backdrop-blur">
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="Auswahl aufheben"
            onClick={() => setSelected(new Set())}
          >
            <X />
          </Button>
          <span className="text-sm font-medium text-zinc-900">
            {countLabel(selected.size, "Datei", "Dateien")} ausgewählt
          </span>
          {selected.size < items.length ? (
            <Button
              variant="link"
              size="sm"
              onClick={() => setSelected(new Set(items.map((item) => item.id)))}
            >
              Alle auswählen
            </Button>
          ) : null}
          <span className="flex-1" />
          {canMove ? (
            <span className="hidden text-xs text-zinc-500 2xl:inline">
              Tipp: Ziehe die Auswahl auf einen Ordner.
            </span>
          ) : null}
          {cutoutCandidates.length > 0 ? (
            <Button
              size="sm"
              variant="secondary"
              loading={cuttingOut}
              onClick={() => void cutOutSelected()}
              title="Von jedem ausgewählten Bild eine Kopie ohne Hintergrund anlegen"
            >
              <Wand2 /> Hintergrund entfernen
            </Button>
          ) : null}
          {canMove ? (
            <Button size="sm" onClick={() => setMovingAssets([...selected])}>
              <FolderInput /> Verschieben nach …
            </Button>
          ) : null}
          {canDelete ? (
            <Button
              size="sm"
              variant="danger"
              loading={removeMany.isPending}
              onClick={() => void trashAssets([...selected])}
              title="In den Papierkorb (Entf) – lässt sich dort wiederherstellen"
            >
              <Trash2 /> In den Papierkorb
            </Button>
          ) : null}
        </div>
      ) : null}

      {showFolders ? (
        <div className="mb-6">
          {items.length > 0 ? (
            <h2 className="mb-2 text-xs font-semibold text-zinc-700">Ordner</h2>
          ) : null}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {subfolders.map((folder) => (
              <FolderTile
                key={folder.id}
                folder={folder}
                subfolders={childFolders(folders, folder.id).length}
                menu={folderMenu(folder)}
                onContextMenu={folderContext.open(folder)}
                draggable={canMove}
                accepts={acceptsDrop}
                onDrop={handleDrop}
                onOpen={() => openFolder(folder.id)}
                onDragStart={(event) => {
                  event.dataTransfer.setData(FOLDER_DRAG_TYPE, folder.id);
                  event.dataTransfer.effectAllowed = "move";
                  draggedFolder.current = folder.id;
                }}
                onDragEnd={() => {
                  draggedFolder.current = null;
                }}
              />
            ))}
          </div>
        </div>
      ) : null}

      {media.isLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {skeletonKeys(10).map((key) => (
            <Skeleton key={key} className="aspect-square" />
          ))}
        </div>
      ) : items.length === 0 ? (
        showFolders && !kind ? null : (
          <EmptyState
            icon={current && !global ? <Folder /> : <Images />}
            title={
              global || kind
                ? "Keine Treffer"
                : current
                  ? "Dieser Ordner ist leer"
                  : "Noch keine Medien"
            }
            description={
              current && !global && !kind
                ? "Lade Dateien hoch oder ziehe sie hierher. Vorhandene Medien verschiebst du über die Auswahl oder per Drag & Drop auf einen Ordner."
                : "Lade Fotos, Videos, Musik oder Grafiken hoch, um sie in deinen Designs und Reels zu verwenden."
            }
            action={
              can("media.upload") ? (
                <Button onClick={() => fileInput.current?.click()}>Dateien hochladen</Button>
              ) : null
            }
          />
        )
      ) : (
        <>
          {showFolders ? (
            <h2 className="mb-2 text-xs font-semibold text-zinc-700">Dateien</h2>
          ) : null}
          <div
            className={cn(
              "grid grid-cols-2 gap-4 transition-opacity sm:grid-cols-3 lg:grid-cols-5",
              media.isPlaceholderData && "opacity-60",
            )}
          >
            {items.map((asset) => (
              <AssetTile
                key={asset.id}
                asset={asset}
                selected={selected.has(asset.id)}
                selectable={canSelect}
                draggable={canMove}
                selecting={selecting}
                onContextMenu={assetMenu.open(asset)}
                folderLabel={
                  global && folders.length > 0 ? folderPathLabel(folders, asset.folderId) : null
                }
                onOpen={(event) => onTileClick(asset, event)}
                onToggle={(event) => toggleSelected(asset.id, event.shiftKey)}
                onOpenFolder={() => {
                  setSearch("");
                  setDebounced("");
                  setProjectId("");
                  openFolder(asset.folderId);
                }}
                onDragStart={(event) => {
                  const ids = selected.has(asset.id) ? [...selected] : [asset.id];
                  event.dataTransfer.setData(ASSETS_DRAG_TYPE, JSON.stringify(ids));
                  event.dataTransfer.effectAllowed = "move";
                  if (ids.length > 1) setDragLabel(event, `${ids.length} Dateien`);
                }}
              />
            ))}
          </div>
          {media.data && media.data.total > items.length ? (
            <div className="mt-6 text-center">
              <Button
                variant="secondary"
                loading={media.isFetching}
                onClick={() => setLimit((value) => value + PAGE_SIZE)}
              >
                Mehr laden ({media.data.total - items.length} weitere)
              </Button>
            </div>
          ) : null}
        </>
      )}

      {dragging ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl border-2 border-dashed border-brand-500 bg-brand-50/80">
          <p className="flex items-center gap-2 text-lg font-medium text-brand-700">
            <Upload className="size-5" />
            {current ? `Zum Hochladen in „${current.name}“ loslassen` : "Zum Hochladen loslassen"}
          </p>
        </div>
      ) : null}
      {opened ? (
        <AssetDialog
          key={opened.id}
          orgId={orgId}
          asset={opened}
          folders={folders}
          onClose={() => setOpened(null)}
          onOpen={setOpened}
        />
      ) : null}
      {creating ? (
        <FolderNameDialog orgId={orgId} parentId={folderId} onClose={() => setCreating(false)} />
      ) : null}
      {renaming ? (
        <FolderNameDialog orgId={orgId} folder={renaming} onClose={() => setRenaming(null)} />
      ) : null}
      {movingFolder ? (
        <FolderPickerDialog
          orgId={orgId}
          title={`„${movingFolder.name}“ verschieben`}
          description="Wähle den Ordner, in den dieser Ordner mit seinem Inhalt soll."
          current={movingFolder.parentId}
          disabled={folderSubtree(folders, movingFolder.id)}
          onPick={(target) => moveFolderTo(movingFolder, target)}
          onClose={() => setMovingFolder(null)}
        />
      ) : null}
      {movingAssets ? (
        <FolderPickerDialog
          orgId={orgId}
          title={`${countLabel(movingAssets.length, "Datei", "Dateien")} verschieben`}
          description="Wähle den Ordner, in den die ausgewählten Dateien sollen."
          current={movingFrom.size === 1 ? [...movingFrom][0] : undefined}
          onPick={(target) => moveAssetsTo(movingAssets, target)}
          onClose={() => setMovingAssets(null)}
        />
      ) : null}
      <ContextMenuPopup at={assetMenu.at} onClose={assetMenu.close}>
        {assetMenu.target ? assetMenuItems(assetMenu.target) : null}
      </ContextMenuPopup>
      <ContextMenuPopup at={folderContext.at} onClose={folderContext.close}>
        {folderContext.target ? folderMenuItems(folderContext.target, true) : null}
      </ContextMenuPopup>
      <ContextMenuPopup at={pageMenu.at} onClose={pageMenu.close}>
        {can("media.upload") ? (
          <DropdownItem onSelect={() => fileInput.current?.click()}>
            <Upload /> Hochladen …
          </DropdownItem>
        ) : null}
        {canMove ? (
          <DropdownItem onSelect={() => setCreating(true)}>
            <FolderPlus /> Neuer Ordner
          </DropdownItem>
        ) : null}
        {canSelect && items.length > 0 ? (
          <DropdownItem onSelect={() => setSelected(new Set(items.map((item) => item.id)))}>
            <CheckSquare /> Alle auswählen
            <span className="ml-auto pl-6 text-xs text-zinc-400">⌘A</span>
          </DropdownItem>
        ) : null}
        {current ? (
          <>
            <DropdownSeparator />
            <DropdownItem onSelect={() => openFolder(current.parentId)}>
              <Folder /> Übergeordneter Ordner
            </DropdownItem>
          </>
        ) : null}
      </ContextMenuPopup>
      {mockupFor ? (
        <MockupDialog
          orgId={orgId}
          asset={mockupFor}
          defaultProjectId={mockupFor.projectId}
          onSaved={can("media.upload") ? () => void invalidate() : undefined}
          onClose={() => setMockupFor(null)}
        />
      ) : null}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        danger
        title={deleting ? `Ordner „${deleting.name}“ löschen?` : "Ordner löschen?"}
        description={deleting ? deleteHint(deleting) : undefined}
        confirmLabel="Ordner löschen"
        onConfirm={async () => {
          if (!deleting) return;
          await removeFolder.mutateAsync({ orgId, folderId: deleting.id });
          // The open folder (or one above it) is gone: go to where its contents went.
          if (path.some((folder) => folder.id === deleting.id)) openFolder(deleting.parentId, true);
          await invalidate();
          toast.success(`Ordner „${deleting.name}“ gelöscht`);
        }}
      />
    </section>
  );
}
